// Sales Dashboard v2 — a fast, server-aggregated rebuild of the classic
// /dashboard/sales-dashboard. Every number is rolled up in ClickHouse (the
// self-hosted warehouse that also powers the MCP) and only the aggregate is
// shipped to the browser, so this stays instant where the classic dashboard
// downloaded every raw invoice line and grouped it in JS.
//
// Backend: SalesDashboardV2Controller  (api/SalesDashboardV2/*)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, IconButton, TextField, MenuItem,
  ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, CircularProgress,
  Skeleton, Avatar, Alert, InputAdornment, Tabs, Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MdAttachMoney, MdInventory2, MdReceiptLong, MdTrendingUp, MdCategory,
  MdPeople, MdLocalShipping, MdRefresh, MdFileDownload, MdArrowUpward,
  MdArrowDownward, MdFilterListOff, MdSearch, MdWarehouse, MdPublic,
  MdShowChart, MdInsights,
} from 'react-icons/md';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, PieChart, Pie, Cell, Legend,
} from 'recharts';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS, apiCall } from '../config/api';
import ReportsSection, { REPORT_TABS, ChartFullscreen, FsButton } from './SalesDashboardV2Reports';
import CompareControl, { PERIOD_COLORS } from './SalesDashboardV2Compare';

// ---- CVD-safe categorical palette (Okabe–Ito), assigned in fixed order ------
const CAT = ['#0072B2', '#E69F00', '#009E73', '#CC79A7', '#56B4E9', '#D55E00', '#F0E442', '#999999'];

// ---- formatters -------------------------------------------------------------
const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usdC = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
const num0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const numC = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const money = (n, compact = false) => (compact ? usdC : usd0).format(Number(n) || 0);
const count = (n, compact = false) => (compact ? numC : num0).format(Number(n) || 0);
const pct = (n) => `${((Number(n) || 0) * 100).toFixed(1)}%`;

// LOCAL calendar date (not toISOString, which is UTC and rolls to tomorrow
// every evening ET — that made "Today" empty and shifted every preset by a day)
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const isIsoDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');

const PRESETS = [
  { key: 'today', label: 'Today', months: 0, days: 0 },
  { key: '7d', label: '7D', months: 0, days: 7 },
  { key: '30d', label: '30D', months: 0, days: 30 },
  { key: '90d', label: '90D', months: 0, days: 90 },
  { key: '6mo', label: '6M', months: 6, days: 0 },
  { key: '12mo', label: '12M', months: 12, days: 0 },
  { key: 'ytd', label: 'YTD', ytd: true },
];

const EMPTY = {
  summary: null, trend: [], forecast: [], topItems: [], topCustomers: [],
  byCollection: [], byWarehouse: [], byState: [], bySize: [], byColor: [],
  byStyle: [], byZip: [], dropship: [],
};

// =============================================================================
//  small presentational pieces
// =============================================================================
function KpiTile({ icon: Icon, label, value, prev, kind = 'money', color }) {
  const theme = useTheme();
  const cur = Number(value) || 0;
  const p = prev == null ? null : Number(prev) || 0;
  const delta = p != null && p !== 0 ? (cur - p) / p : null;
  const up = delta != null && delta >= 0;
  const deltaColor = delta == null ? theme.palette.text.disabled : up ? theme.palette.success.main : theme.palette.error.main;
  return (
    <Paper elevation={0} sx={{
      p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`,
      display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0,
      transition: 'border-color .15s', '&:hover': { borderColor: color || theme.palette.primary.main },
    }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{
          width: 30, height: 30, borderRadius: 2, display: 'grid', placeItems: 'center',
          bgcolor: (color || theme.palette.primary.main) + '1f', color: color || theme.palette.primary.main, flexShrink: 0,
        }}><Icon size={17} /></Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: .3, textTransform: 'uppercase', fontSize: 11 }} noWrap>{label}</Typography>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }} noWrap>
        {kind === 'money' ? money(cur, true) : kind === 'pct' ? pct(cur) : count(cur)}
      </Typography>
      {delta != null ? (
        <Stack direction="row" alignItems="center" spacing={0.4} sx={{ color: deltaColor }}>
          {up ? <MdArrowUpward size={14} /> : <MdArrowDownward size={14} />}
          <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {Math.abs(delta * 100).toFixed(1)}%
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>vs prev</Typography>
        </Stack>
      ) : (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>current period</Typography>
      )}
    </Paper>
  );
}

function Panel({ title, subtitle, icon: Icon, right, children, sx }) {
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, display: 'flex', flexDirection: 'column', minWidth: 0, ...sx }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          {Icon && <Icon size={18} color={theme.palette.primary.main} />}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{title}</Typography>
            {subtitle && <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>{subtitle}</Typography>}
          </Box>
        </Stack>
        {right}
      </Stack>
      {children}
    </Paper>
  );
}

// Horizontal ranked bar list (revenue bar behind label + value). Cleaner and
// lighter than a chart for top-N breakdowns, and lets us drop in thumbnails.
function RankBars({ rows, labelKey, valueKey, subKey, color, formatter = (v) => money(v, true), emptyText = 'No data', thumbs = false }) {
  const theme = useTheme();
  const max = Math.max(1, ...rows.map((r) => Number(r[valueKey]) || 0));
  if (!rows.length) return <Typography variant="body2" sx={{ color: 'text.disabled', py: 3, textAlign: 'center' }}>{emptyText}</Typography>;
  return (
    <Stack spacing={1}>
      {rows.map((r, i) => {
        const v = Number(r[valueKey]) || 0;
        const w = `${Math.max(2, (v / max) * 100)}%`;
        const c = color || CAT[i % CAT.length];
        return (
          <Box key={(r[labelKey] || i) + '-' + i} sx={{ position: 'relative' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
              {thumbs && (
                <Avatar variant="rounded" src={r.thumb || undefined} sx={{ width: 30, height: 30, bgcolor: theme.palette.action.hover, fontSize: 10 }}>
                  {(r[labelKey] || '?').slice(0, 2).toUpperCase()}
                </Avatar>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{r[labelKey] || '—'}</Typography>
                {subKey && r[subKey] ? <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>{r[subKey]}</Typography> : null}
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{formatter(v)}</Typography>
            </Stack>
            <Box sx={{ height: 6, borderRadius: 3, bgcolor: theme.palette.action.hover, overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: w, bgcolor: c, borderRadius: 3, transition: 'width .4s ease' }} />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

// =============================================================================
//  main
// =============================================================================
export default function SalesDashboardV2() {
  const theme = useTheme();

  const [filters, setFilters] = useState(() => ({
    // Default to the last 90 days: the date projection serves it in ~0.2s
    // (a 12-month view scans ~half the 9.75M-row table and is a click away).
    preset: '90d',
    from: iso(addDays(new Date(), -90)),
    to: iso(new Date()),
    source: 'all',
    customer: '',
    collection: '',
    warehouse: '',
    state: '',
  }));
  // Customer / collection filters are chip lists (multiple terms OR'd together,
  // like the classic dashboard) plus a live-typed term, debounced into `filters`
  // as a newline-joined string the backend splits.
  const [custText, setCustText] = useState('');
  const [collText, setCollText] = useState('');
  const [custChips, setCustChips] = useState([]);
  const [collChips, setCollChips] = useState([]);

  const [data, setData] = useState(EMPTY);
  const [opts, setOpts] = useState({ warehouses: [], states: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trendMetric, setTrendMetric] = useState('revenue');
  const [attrTab, setAttrTab] = useState('size');
  const [currentTab, setCurrentTab] = useState(0);   // 0 = Overview, 1.. = report tabs
  const [compare, setCompare] = useState(false);      // legacy vs-previous flag (superseded by cmpPeriods)
  // GLOBAL multi-period compare — [{from,to,label}] (max 10). Drives the report
  // tabs' $/Δ% columns, the Overview trend overlay + KPI deltas, and the
  // per-tab Graph view.
  const [cmpPeriods, setCmpPeriods] = useState([]);
  const [cmpTrends, setCmpTrends] = useState([]);     // [{label, rows}] one per period (Overview overlay)
  const [cmpSummary, setCmpSummary] = useState(null); // first period's summary (KPI re-anchor)
  const [fsTrend, setFsTrend] = useState(false);      // fullscreen trend dialog
  const [months, setMonths] = useState([]);           // available months for the Month filter
  const [dataMin, setDataMin] = useState('');         // warehouse data floor (for compare-period warnings)
  const abortRef = useRef(null);

  const grain = useMemo(() => {
    const days = (new Date(filters.to) - new Date(filters.from)) / 86400000;
    if (days <= 92) return 'day';
    return 'month';
  }, [filters.from, filters.to]);

  const qs = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    p.set('from', filters.from); p.set('to', filters.to);
    if (filters.source !== 'all') p.set('source', filters.source);
    if (filters.customer) p.set('customer', filters.customer);
    if (filters.collection) p.set('collection', filters.collection);
    if (filters.warehouse) p.set('warehouse', filters.warehouse);
    if (filters.state) p.set('state', filters.state);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [filters]);

  // ---- data load (declared before the effect that calls it — TDZ safety) ----
  const loadAll = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true); setError('');
    const get = (ep, extra) => apiCall(`${ep}?${qs(extra)}`, { signal: ac.signal });
    const arr = (r) => (r && Array.isArray(r.data) ? r.data : []);
    // Each panel settles independently (one slow/failed query must not blank the
    // page), and we load in two waves so the hero (KPIs + trend + top lists)
    // paints fast instead of waiting on all ~13 full-scan queries at once. The
    // attribute tabs load lazily in their own effect.
    const run = async (jobs) => {
      const settled = await Promise.allSettled(jobs.map((j) => j[1]));
      if (abortRef.current !== ac) return null; // superseded by a newer load
      const patch = {};
      const failed = [];
      settled.forEach((res, i) => {
        const [key, , pick] = jobs[i];
        if (res.status === 'fulfilled') patch[key] = pick(res.value);
        else if (res.reason && res.reason.name !== 'AbortError') failed.push(key);
      });
      setData((prev) => ({ ...prev, ...patch }));
      return failed;
    };
    // Wave 1 — the hero, rendered as soon as it lands.
    const f1 = await run([
      ['summary', get(API_ENDPOINTS.SALES_V2_SUMMARY), (r) => arr(r)[0] || null],
      ['trend', get(API_ENDPOINTS.SALES_V2_TREND, { grain }), arr],
      ['forecast', get(API_ENDPOINTS.SALES_V2_FORECAST, { horizon: 3 }), (r) => (r && r.forecast) || []],
      ['topItems', get(API_ENDPOINTS.SALES_V2_TOP_ITEMS, { limit: 12 }), (r) => (r && r.data) || []],
      ['topCustomers', get(API_ENDPOINTS.SALES_V2_TOP_CUSTOMERS, { limit: 12 }), arr],
    ]);
    if (f1 === null) return;
    setLoading(false); // hero is on screen; the breakdowns stream in below
    // Wave 2 — the breakdown panels.
    const f2 = await run([
      ['byCollection', get(API_ENDPOINTS.SALES_V2_BY_COLLECTION, { limit: 10 }), arr],
      ['byWarehouse', get(API_ENDPOINTS.SALES_V2_BY_WAREHOUSE, { limit: 12 }), arr],
      ['dropship', get(API_ENDPOINTS.SALES_V2_DROPSHIP_SPLIT), arr],
      ['byState', get(API_ENDPOINTS.SALES_V2_BY_STATE, { limit: 12 }), arr],
      ['byZip', get(API_ENDPOINTS.SALES_V2_BY_ZIP, { limit: 15 }), arr],
    ]);
    if (f2 === null) return;
    const failed = [...f1, ...f2];
    setError(failed.length ? `Couldn't refresh: ${failed.join(', ')} — showing the rest.` : '');
  }, [qs, grain]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Active attribute (size/color/style) loads lazily — only the visible tab,
  // re-fetching when the filters or the selected tab change. Keeps 2 queries
  // off every dashboard load.
  useEffect(() => {
    let live = true;
    const key = attrTab === 'color' ? 'byColor' : attrTab === 'style' ? 'byStyle' : 'bySize';
    const ep = attrTab === 'color' ? API_ENDPOINTS.SALES_V2_BY_COLOR
      : attrTab === 'style' ? API_ENDPOINTS.SALES_V2_BY_STYLE : API_ENDPOINTS.SALES_V2_BY_SIZE;
    apiCall(`${ep}?${qs({ limit: 10 })}`)
      .then((r) => { if (live) setData((prev) => ({ ...prev, [key]: (r && Array.isArray(r.data) ? r.data : []) })); })
      .catch(() => {});
    return () => { live = false; };
  }, [qs, attrTab]);

  // filter option lists (warehouses + states), refreshed when the window moves
  useEffect(() => {
    let live = true;
    apiCall(`${API_ENDPOINTS.SALES_V2_FILTERS}?from=${filters.from}&to=${filters.to}`)
      .then((r) => { if (live && r) setOpts({ warehouses: r.warehouses || [], states: r.states || [] }); })
      .catch(() => {});
    return () => { live = false; };
  }, [filters.from, filters.to]);

  // debounce chips + live text → filters (newline-joined multi-terms)
  useEffect(() => {
    const joined = [...custChips, custText.trim()].filter(Boolean).join('\n');
    const t = setTimeout(() => setFilters((f) => (f.customer === joined ? f : { ...f, customer: joined })), 450);
    return () => clearTimeout(t);
  }, [custText, custChips]);
  useEffect(() => {
    const joined = [...collChips, collText.trim()].filter(Boolean).join('\n');
    const t = setTimeout(() => setFilters((f) => (f.collection === joined ? f : { ...f, collection: joined })), 450);
    return () => clearTimeout(t);
  }, [collText, collChips]);

  // available months for the Month quick-filter (from the warehouse's real span)
  useEffect(() => {
    let live = true;
    apiCall(API_ENDPOINTS.SALES_V2_DATE_RANGE)
      .then((r) => {
        if (!live) return;
        const row = r && Array.isArray(r.data) ? r.data[0] : null;
        if (!row || !row.minDate || !row.maxDate) return;
        setDataMin(row.minDate);
        const out = [];
        const d = new Date(row.maxDate + 'T00:00:00Z');
        const min = new Date(row.minDate + 'T00:00:00Z');
        while (d >= min && out.length < 36) {
          out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
          d.setUTCMonth(d.getUTCMonth() - 1);
        }
        setMonths(out);
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // ---- global compare-period data (Overview trend overlay + KPI re-anchor) --
  // One /trend per period (all hit the date projection) + /summary for the
  // first period. Overlays align by day/month INDEX so different calendar
  // windows sit on one x-axis.
  useEffect(() => {
    if (!cmpPeriods.length) { setCmpTrends([]); setCmpSummary(null); return undefined; }
    let live = true;
    Promise.allSettled([
      ...cmpPeriods.map((p) =>
        apiCall(`${API_ENDPOINTS.SALES_V2_TREND}?${qs({ grain, from: p.from, to: p.to })}`)
          .then((r) => ({ label: p.label, rows: (r && Array.isArray(r.data)) ? r.data : [] }))),
      apiCall(`${API_ENDPOINTS.SALES_V2_SUMMARY}?${qs({ from: cmpPeriods[0].from, to: cmpPeriods[0].to })}`)
        .then((r) => (r && Array.isArray(r.data) ? r.data[0] : null)),
    ]).then((settled) => {
      if (!live) return;
      const trendRes = settled.slice(0, cmpPeriods.length)
        .map((s, i) => (s.status === 'fulfilled' ? s.value : { label: cmpPeriods[i].label, rows: [] }));
      const sumRes = settled[cmpPeriods.length];
      setCmpTrends(trendRes);
      setCmpSummary(sumRes.status === 'fulfilled' ? sumRes.value : null);
    });
    return () => { live = false; };
  }, [cmpPeriods, qs, grain]);

  // ---- filter helpers -------------------------------------------------------
  const applyPreset = (key) => {
    const now = new Date();
    let from;
    const preset = PRESETS.find((p) => p.key === key);
    if (preset?.ytd) from = new Date(now.getFullYear(), 0, 1);
    else if (preset?.months) from = addMonths(now, -preset.months);
    else from = addDays(now, -(preset?.days ?? 30));
    setFilters((f) => ({ ...f, preset: key, from: iso(from), to: iso(now) }));
  };
  const applyMonth = (m) => {
    if (!m) { applyPreset('90d'); return; } // "All months" resets to the default window
    const [y, mo] = m.split('-').map(Number);
    const first = new Date(y, mo - 1, 1);
    const last = new Date(y, mo, 0);
    const today = new Date();
    setFilters((f) => ({ ...f, preset: 'custom', from: iso(first), to: iso(last > today ? today : last) }));
  };
  // which month the current from/to exactly spans (for the Month select value)
  const monthValue = useMemo(() => {
    if (!isIsoDate(filters.from) || !isIsoDate(filters.to)) return ''; // cleared/partial date input
    const [y, mo] = filters.from.split('-').map(Number);
    if (!y || !mo || mo < 1 || mo > 12) return '';
    const first = new Date(y, mo - 1, 1);
    const last = new Date(y, mo, 0);
    const today = new Date();
    const expectTo = iso(last > today ? today : last);
    return filters.from === iso(first) && filters.to === expectTo ? `${y}-${String(mo).padStart(2, '0')}` : '';
  }, [filters.from, filters.to]);

  const setField = (k, v) => setFilters((f) => ({ ...f, [k]: v, preset: k === 'from' || k === 'to' ? 'custom' : f.preset }));
  const clearFilters = () => {
    setCustText(''); setCollText(''); setCustChips([]); setCollChips([]); setCompare(false);
    setFilters({ preset: '90d', from: iso(addDays(new Date(), -90)), to: iso(new Date()), source: 'all', customer: '', collection: '', warehouse: '', state: '' });
  };
  const rangeDays = useMemo(() => {
    const d = Math.round((new Date(filters.to) - new Date(filters.from)) / 86400000) + 1;
    return Number.isFinite(d) ? Math.max(1, d) : 1;
  }, [filters.from, filters.to]);
  const activeChips = [
    filters.source !== 'all' && { k: 'source', label: filters.source === 'ds' ? 'Drop-ship' : 'Non-drop-ship' },
    filters.warehouse && { k: 'warehouse', label: `WHS: ${filters.warehouse}` },
    filters.state && { k: 'state', label: `State: ${filters.state}` },
  ].filter(Boolean);
  const addChip = (setChips, setText) => (val) => {
    const v = (val || '').trim();
    if (!v) return;
    setChips((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setText('');
  };
  const addCustChip = addChip(setCustChips, setCustText);
  const addCollChip = addChip(setCollChips, setCollText);

  // ---- trend dataset (complete periods + a flagged partial tail + forecast) --
  // The trailing bucket is the still-open current period (to always = today), so
  // it is plotted as a distinct dashed "partial" segment rather than as part of
  // the solid area (otherwise it always looks like a crash). The monthly forecast
  // only overlays a monthly-grain revenue view, bridged from the last COMPLETE
  // period.
  const trendData = useMemo(() => {
    const rows = data.trend.map((r) => ({ period: r.period, value: Number(r[trendMetric]) || 0 }));
    if (!rows.length) return [];
    // string compares against the LOCAL calendar date — no UTC rollover drift
    const todayIso = iso(new Date());
    const isPartial = (p) => (grain === 'day'
      ? String(p).slice(0, 10) === todayIso
      : String(p).slice(0, 7) === todayIso.slice(0, 7));
    const lastPartial = isPartial(rows[rows.length - 1].period);
    const out = rows.map((r, i) => {
      const partialHere = lastPartial && i === rows.length - 1;
      const prevToPartial = lastPartial && i === rows.length - 2;
      return {
        period: r.period,
        actual: partialHere ? null : r.value,
        partial: partialHere || prevToPartial ? r.value : null, // dashed tail connects prev→partial
        forecast: null,
      };
    });
    if (trendMetric === 'revenue' && grain === 'month' && data.forecast.length) {
      const lastCompleteIdx = lastPartial ? out.length - 2 : out.length - 1;
      if (lastCompleteIdx >= 0) {
        out[lastCompleteIdx].forecast = rows[lastCompleteIdx].value; // bridge from last complete
        const byPeriod = new Map(out.map((o) => [o.period, o]));
        data.forecast.forEach((f) => {
          const v = Number(f.revenue) || 0;
          if (byPeriod.has(f.month)) byPeriod.get(f.month).forecast = v;
          else out.push({ period: f.month, actual: null, partial: null, forecast: v });
        });
      }
    }
    return out;
  }, [data.trend, data.forecast, trendMetric, grain]);

  const hasForecast = trendMetric === 'revenue' && grain === 'month' && data.forecast.length > 0;

  // Comparison overlays for the trend hero, aligned by DATE OFFSET from each
  // window's start (day N over day N, month N over month N) — NOT by array
  // index: the trend endpoint skips zero-sale days, so index pairing would
  // shift a filtered/sparse series onto the wrong dates. Each point keeps its
  // own real date for the tooltip.
  const trendChartData = useMemo(() => {
    if (!cmpTrends.length) return trendData;
    const off = (p, start) => {
      if (!p || !start) return -1;
      const a = new Date(String(p).slice(0, 10)); const b = new Date(String(start).slice(0, 10));
      return grain === 'day'
        ? Math.round((a - b) / 86400000)
        : (a.getUTCFullYear() - b.getUTCFullYear()) * 12 + (a.getUTCMonth() - b.getUTCMonth());
    };
    const out = trendData.map((r) => ({ ...r }));
    const idxByOff = new Map();
    out.forEach((r, i) => { if (r.period) idxByOff.set(off(r.period, filters.from), i); });
    cmpTrends.forEach((t, k) => {
      const start = cmpPeriods[k]?.from;
      t.rows.forEach((row) => {
        const v = Number(row[trendMetric]) || 0;
        const o = off(row.period, start);
        if (idxByOff.has(o)) {
          const r = out[idxByOff.get(o)];
          r[`p${k}`] = v; r[`p${k}date`] = row.period;
        } else {
          out.push({ period: null, actual: null, partial: null, forecast: null, [`p${k}`]: v, [`p${k}date`]: row.period });
        }
      });
    });
    return out;
  }, [trendData, cmpTrends, trendMetric, grain, filters.from, cmpPeriods]);

  const fmtPeriod = useCallback((v) => {
    if (!v) return '';
    const d = new Date(v);
    if (grain === 'day') return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  }, [grain]);

  const trendFmt = trendMetric === 'revenue' ? money : count;

  // ---- export ---------------------------------------------------------------
  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const s = data.summary || {};
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{
      From: filters.from, To: filters.to, Source: filters.source,
      Revenue: s.revenue, Units: s.units, Invoices: s.invoices,
      AvgOrderValue: s.invoices ? (s.revenue / s.invoices) : 0,
      UniqueSKUs: s.skus, Customers: s.customers, Warehouses: s.warehouses,
      DropShipRevenue: s.dsRevenue,
    }]), 'Summary');
    const add = (rows, name) => { if (rows?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name); };
    add(data.trend, 'Trend');
    add(data.topItems, 'Top SKUs');
    add(data.topCustomers, 'Top Customers');
    add(data.byCollection, 'By Collection');
    add(data.byWarehouse, 'By Warehouse');
    add(data.byState, 'By State');
    add(data.bySize, 'By Size');
    add(data.byColor, 'By Color');
    add(data.byStyle, 'By Style');
    add(data.byZip, 'By Zip');
    XLSX.writeFile(wb, `Sales_Dashboard_${filters.from}_to_${filters.to}.xlsx`);
  };

  // ---- derived KPI values ---------------------------------------------------
  const s = data.summary || {};
  const aov = s.invoices ? s.revenue / s.invoices : 0;
  const prevAov = s.prevInvoices ? s.prevRevenue / s.prevInvoices : 0;
  const dsShare = s.revenue ? (s.dsRevenue || 0) / s.revenue : 0;

  const attrRows = attrTab === 'size' ? data.bySize : attrTab === 'color' ? data.byColor : data.byStyle;
  // fixed colour per category (never by rank) so slices don't repaint between periods
  const DS_COLORS = { 'Drop-ship': CAT[0], 'Non-drop-ship': CAT[1], Other: CAT[7] };
  const dropshipPie = data.dropship.map((d) => ({ name: d.source, value: Number(d.revenue) || 0, fill: DS_COLORS[d.source] || CAT[7] }));

  const gridSurface = theme.palette.divider;
  const axisColor = theme.palette.text.secondary;

  // ---- render ---------------------------------------------------------------
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      {/* header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MdInsights size={26} color={theme.palette.primary.main} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Sales Dashboard</Typography>
            <Chip label="v2" size="small" color="primary" variant="outlined" sx={{ fontWeight: 700, height: 20 }} />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Live from the sales warehouse · {data.summary ? `${count(9752000, true)}+ invoice lines` : 'loading…'}
          </Typography>
        </Box>
        {currentTab === 0 && (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" startIcon={<MdFileDownload />} onClick={exportExcel} disabled={loading || !data.summary}>Export</Button>
            <Button size="small" variant="contained" startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <MdRefresh />} onClick={loadAll} disabled={loading}>Refresh</Button>
          </Stack>
        )}
      </Stack>

      {/* filter bar */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5}>
          <ToggleButtonGroup size="small" exclusive value={filters.preset} onChange={(e, v) => v && applyPreset(v)}>
            {PRESETS.map((p) => <ToggleButton key={p.key} value={p.key} sx={{ px: 1.2, fontWeight: 700 }}>{p.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => setField('from', e.target.value)} sx={{ width: 150 }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => setField('to', e.target.value)} sx={{ width: 150 }} />
          <TextField size="small" select label="Source" value={filters.source} onChange={(e) => setField('source', e.target.value)} sx={{ width: 150 }}>
            <MenuItem value="all">All sales</MenuItem>
            <MenuItem value="ds">Drop-ship</MenuItem>
            <MenuItem value="nonds">Non-drop-ship</MenuItem>
          </TextField>
          <TextField size="small" select label="Warehouse" value={filters.warehouse} onChange={(e) => setField('warehouse', e.target.value)} sx={{ width: 190 }}>
            <MenuItem value="">All warehouses</MenuItem>
            {opts.warehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.id}{w.label ? ` · ${w.label}` : ''}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="State" value={filters.state} onChange={(e) => setField('state', e.target.value)} sx={{ width: 120 }}>
            <MenuItem value="">All states</MenuItem>
            {opts.states.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Month" value={monthValue} onChange={(e) => applyMonth(e.target.value)} sx={{ width: 140 }}>
            <MenuItem value="">All months</MenuItem>
            {months.map((m) => {
              const [y, mo] = m.split('-');
              return <MenuItem key={m} value={m}>{new Date(y, mo - 1, 1).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}</MenuItem>;
            })}
          </TextField>
          <TextField size="small" placeholder="Add customer… ⏎" value={custText}
            onChange={(e) => setCustText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCustChip(custText); }}
            sx={{ width: 170 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }} />
          <TextField size="small" placeholder="Add collection… ⏎" value={collText}
            onChange={(e) => setCollText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCollChip(collText); }}
            sx={{ width: 170 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }} />
          <CompareControl
            periods={cmpPeriods}
            onChange={setCmpPeriods}
            range={{ from: filters.from, to: filters.to }}
            months={months}
            dataMin={dataMin}
          />
          {(activeChips.length > 0 || custChips.length > 0 || collChips.length > 0) && (
            <MuiTooltip title="Clear all filters">
              <IconButton size="small" onClick={clearFilters}><MdFilterListOff /></IconButton>
            </MuiTooltip>
          )}
        </Stack>
        {(activeChips.length > 0 || custChips.length > 0 || collChips.length > 0) && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
            {activeChips.map((c) => (
              <Chip key={c.k} size="small" label={c.label} onDelete={() => setField(c.k, c.k === 'source' ? 'all' : '')} />
            ))}
            {custChips.map((c, i) => (
              <Chip key={`cust-${i}`} size="small" color="primary" variant="outlined" label={`Customer: ${c}`}
                onDelete={() => setCustChips((prev) => prev.filter((_, j) => j !== i))} />
            ))}
            {collChips.map((c, i) => (
              <Chip key={`coll-${i}`} size="small" color="success" variant="outlined" label={`Collection: ${c}`}
                onDelete={() => setCollChips((prev) => prev.filter((_, j) => j !== i))} />
            ))}
          </Stack>
        )}
      </Paper>

      {/* tab bar: Overview + the classic report tabs, all ClickHouse-served */}
      <Paper elevation={0} sx={{ mb: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontSize: 13, fontWeight: 600 } }}>
          <Tab label="Overview" />
          {REPORT_TABS.map((t) => <Tab key={t.key} label={t.label} />)}
        </Tabs>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {currentTab > 0 && (
        <ReportsSection
          tabKey={REPORT_TABS[currentTab - 1].key}
          qs={qs}
          compare={compare}
          rangeDays={rangeDays}
          dropship={data.dropship}
          onDrillState={(st) => setField('state', st)}
          stateFilter={filters.state}
          cmpPeriods={cmpPeriods}
          range={{ from: filters.from, to: filters.to }}
        />
      )}

      {currentTab === 0 && (<>
      {/* KPI row — with compare periods active, deltas re-anchor to the FIRST
          selected period instead of the default adjacent-previous window */}
      {cmpSummary && cmpPeriods.length > 0 && (
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 0.75 }}>
          KPI arrows compare against <b>{cmpPeriods[0].label}</b> ({cmpPeriods[0].from} → {cmpPeriods[0].to})
        </Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', lg: 'repeat(7,1fr)' }, gap: 1.5, mb: 2 }}>
        {loading && !data.summary
          ? Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} variant="rounded" height={104} sx={{ borderRadius: 3 }} />)
          : (<>
            <KpiTile icon={MdAttachMoney} label="Revenue" value={s.revenue} prev={cmpSummary ? cmpSummary.revenue : s.prevRevenue} kind="money" color={CAT[0]} />
            <KpiTile icon={MdInventory2} label="Units" value={s.units} prev={cmpSummary ? cmpSummary.units : s.prevUnits} kind="count" color={CAT[2]} />
            <KpiTile icon={MdReceiptLong} label="Invoices" value={s.invoices} prev={cmpSummary ? cmpSummary.invoices : s.prevInvoices} kind="count" color={CAT[4]} />
            <KpiTile icon={MdTrendingUp} label="Avg Order" value={aov} prev={cmpSummary ? (cmpSummary.invoices ? cmpSummary.revenue / cmpSummary.invoices : null) : prevAov} kind="money" color={CAT[1]} />
            <KpiTile icon={MdCategory} label="SKUs" value={s.skus} prev={cmpSummary ? cmpSummary.skus : undefined} kind="count" color={CAT[3]} />
            <KpiTile icon={MdPeople} label="Customers" value={s.customers} prev={cmpSummary ? cmpSummary.customers : undefined} kind="count" color={CAT[5]} />
            <KpiTile icon={MdLocalShipping} label="Drop-ship" value={dsShare} kind="pct" color={CAT[7]} />
          </>)}
      </Box>

      {/* trend hero */}
      <Panel
        title="Revenue trend"
        subtitle={`${grain === 'day' ? 'Daily' : 'Monthly'}${hasForecast ? ' · dashed = 3-mo projection' : ''} · dotted tail = current period${cmpTrends.length ? ` · ${cmpTrends.length} comparison period${cmpTrends.length > 1 ? 's' : ''} overlaid, aligned by ${grain}` : ''}`}
        icon={MdShowChart}
        sx={{ mb: 2 }}
        right={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ToggleButtonGroup size="small" exclusive value={trendMetric} onChange={(e, v) => v && setTrendMetric(v)}>
              <ToggleButton value="revenue" sx={{ px: 1.2, textTransform: 'none' }}>Revenue</ToggleButton>
              <ToggleButton value="units" sx={{ px: 1.2, textTransform: 'none' }}>Units</ToggleButton>
              <ToggleButton value="invoices" sx={{ px: 1.2, textTransform: 'none' }}>Invoices</ToggleButton>
            </ToggleButtonGroup>
            <FsButton onClick={() => setFsTrend(true)} />
          </Box>
        }
      >
        {(() => {
          const chartEl = (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trendChartData} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
                  <defs>
                    <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridSurface} vertical={false} />
                  <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fill: axisColor, fontSize: 12 }} tickLine={false} axisLine={{ stroke: gridSurface }} minTickGap={20} />
                  <YAxis tickFormatter={(v) => trendMetric === 'revenue' ? money(v, true) : count(v, true)} tick={{ fill: axisColor, fontSize: 12 }} tickLine={false} axisLine={false} width={54} />
                  <Tooltip
                    contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 10, fontSize: 13 }}
                    labelStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
                    itemStyle={{ color: theme.palette.text.primary }}
                    labelFormatter={fmtPeriod}
                    formatter={(v, n, entry) => {
                      if (v == null) return ['—', n];
                      const m = /^p(\d+)$/.exec(entry?.dataKey || '');
                      if (m) {
                        const d = entry?.payload?.[`p${m[1]}date`];
                        return [trendFmt(v), `${n}${d ? ` (${fmtPeriod(d)})` : ''}`];
                      }
                      return [trendFmt(v), n === 'forecast' ? 'Projected' : n === 'partial' ? 'Current (partial)' : 'Actual'];
                    }}
                  />
                  {cmpTrends.length > 0 && <Legend wrapperStyle={{ fontSize: 12 }} />}
                  <Area type="monotone" dataKey="actual" name="Current" stroke={theme.palette.primary.main} strokeWidth={2.5} fill="url(#revFill)" dot={false} activeDot={{ r: 4 }} connectNulls />
                  <Line type="monotone" dataKey="partial" name="partial" legendType="none" stroke={theme.palette.primary.main} strokeWidth={2} strokeDasharray="2 3" dot={{ r: 2.5, fill: theme.palette.primary.main }} connectNulls />
                  {hasForecast && <Line type="monotone" dataKey="forecast" name="forecast" legendType="none" stroke={theme.palette.primary.main} strokeWidth={2} strokeDasharray="6 5" dot={false} connectNulls />}
                  {cmpTrends.map((t, k) => (
                    <Line key={`p${k}`} type="monotone" dataKey={`p${k}`} name={t.label}
                      stroke={PERIOD_COLORS[k % PERIOD_COLORS.length]} strokeWidth={1.8}
                      strokeDasharray="5 3" dot={false} activeDot={{ r: 3 }} connectNulls />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
          );
          return (<>
            <Box sx={{ height: 320, width: '100%' }}>
              {loading && !data.trend.length ? <Skeleton variant="rounded" height={300} />
                : !trendData.length ? (
                  <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>No sales in this range</Typography>
                  </Box>
                ) : chartEl}
            </Box>
            <ChartFullscreen open={fsTrend} onClose={() => setFsTrend(false)}
              title={`Revenue trend${cmpTrends.length ? ` — ${cmpTrends.length} comparison period${cmpTrends.length > 1 ? 's' : ''} overlaid` : ''}`}>
              {chartEl}
            </ChartFullscreen>
          </>);
        })()}
      </Panel>

      {/* Top SKUs + Top Customers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
        <Panel title="Top SKUs" subtitle="by revenue" icon={MdInventory2}>
          {loading && !data.topItems.length ? <Skeleton variant="rounded" height={360} />
            : <RankBars rows={data.topItems.map((r) => ({ ...r, label: r.item, sub: [r.collection, r.size].filter(Boolean).join(' · ') }))}
                labelKey="label" subKey="sub" valueKey="revenue" color={CAT[0]} thumbs emptyText="No SKUs in range" />}
        </Panel>
        <Panel title="Top Customers" subtitle="by revenue" icon={MdPeople}>
          {loading && !data.topCustomers.length ? <Skeleton variant="rounded" height={360} />
            : <RankBars rows={data.topCustomers.map((r) => ({ ...r, label: r.company || r.customerId, sub: `${count(r.orders)} orders · AOV ${money(r.aov)}` }))}
                labelKey="label" subKey="sub" valueKey="revenue" color={CAT[2]} emptyText="No customers in range" />}
        </Panel>
      </Box>

      {/* Collection / Warehouse / Drop-ship */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1.1fr 1.1fr 0.8fr' }, gap: 2, mb: 2 }}>
        <Panel title="Top Collections" icon={MdCategory}>
          {loading && !data.byCollection.length ? <Skeleton variant="rounded" height={300} />
            : <RankBars rows={data.byCollection.map((r) => ({ ...r, label: r.x }))} labelKey="label" valueKey="revenue" color={CAT[1]} />}
        </Panel>
        <Panel title="By Warehouse" icon={MdWarehouse}>
          {loading && !data.byWarehouse.length ? <Skeleton variant="rounded" height={300} />
            : <RankBars rows={data.byWarehouse.map((r) => ({ ...r, label: r.warehouse, sub: [r.label, r.state].filter(Boolean).join(', ') }))} labelKey="label" subKey="sub" valueKey="revenue" color={CAT[4]} />}
        </Panel>
        <Panel title="Drop-ship split" subtitle="share of revenue" icon={MdLocalShipping}>
          {loading && !data.dropship.length ? <Skeleton variant="rounded" height={300} />
            : !dropshipPie.length ? (
              <Box sx={{ height: 240, display: 'grid', placeItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>No data</Typography>
              </Box>
            ) : (
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={dropshipPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2} stroke={theme.palette.background.paper} strokeWidth={2}>
                      {dropshipPie.map((e, i) => <Cell key={i} fill={e.fill} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 10 }} />
                    <Legend verticalAlign="bottom" height={28} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}
        </Panel>
      </Box>

      {/* States + Attribute tabs + Zips */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>
        <Panel title="Top Ship-to States" icon={MdPublic}>
          {loading && !data.byState.length ? <Skeleton variant="rounded" height={300} />
            : <RankBars rows={data.byState.map((r) => ({ ...r, label: r.x }))} labelKey="label" valueKey="revenue" color={CAT[5]} />}
        </Panel>
        <Panel title="Product Attributes" icon={MdInsights}
          right={
            <ToggleButtonGroup size="small" exclusive value={attrTab} onChange={(e, v) => v && setAttrTab(v)}>
              <ToggleButton value="size" sx={{ px: 1, textTransform: 'none' }}>Size</ToggleButton>
              <ToggleButton value="color" sx={{ px: 1, textTransform: 'none' }}>Color</ToggleButton>
              <ToggleButton value="style" sx={{ px: 1, textTransform: 'none' }}>Style</ToggleButton>
            </ToggleButtonGroup>
          }>
          {loading && !attrRows.length ? <Skeleton variant="rounded" height={300} />
            : <RankBars rows={attrRows.map((r) => ({ ...r, label: r.x }))} labelKey="label" valueKey="revenue" color={CAT[3]} emptyText="No data" />}
        </Panel>
        <Panel title="Top Ship-to Zips" icon={MdPublic}>
          {loading && !data.byZip.length ? <Skeleton variant="rounded" height={300} />
            : <RankBars rows={data.byZip.map((r) => ({ ...r, label: r.zip, sub: r.state }))} labelKey="label" subKey="sub" valueKey="revenue" color={CAT[0]} />}
        </Panel>
      </Box>
      </>)}

      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 2, textAlign: 'center' }}>
        Revenue = extended line price (ExtPrice) from RPTSalesInvoiceSummary · far-future/garbage dates excluded · aggregated in ClickHouse
      </Typography>
    </Box>
  );
}
